import { useCallback, useEffect, useState } from 'react';
import type { Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';

import { num2str } from 'CorePackage/MainModule/common/num2str';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';
import { handleSortChange } from 'CorePackage/MainModule/common/handleSortChange';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { Table } from 'CorePackage/TableModule/components/Table';
import { IconMenu } from 'UIPackage/IconsModule/components/IconMenu';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { ExportItemEnum } from 'APIPackage/ImportExportModule/enums/ExportItemEnum';
import { TablePage } from 'CorePackage/TableModule/components/TablePage';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { initialExportTableColumns } from '../constants/initialExportTableColumns';
import { ExportPanel } from './ExportPanel';
import styles from './ExportPage.module.scss';
import columnStyles from './ExportTable.module.scss';
import {
  exportGetData,
  exportGetDataNextPage,
  exportInitPage,
  exportRemove,
  exportSetOrder,
} from '../actions/exportActions';
import { getExportData, getExportTotalCount } from '../selectors/exportSelectors';
import { IExportRow } from '../interfaces/IExportRow';
import { mapStatusNamesByCode } from '../constants/mapStatusNamesByCode';

const rowAttributes = (data: IExportRow) => {
  const statusCode = mapStatusNamesByCode[data.status];

  return {
    ...(statusCode === ExportItemEnum.ERROR && { 'data-error': true }),
    ...(statusCode === ExportItemEnum.IN_PROGRESS && { 'data-progress': true }),
    ...(statusCode === ExportItemEnum.DONE && { 'data-done': true }),
  };
};

interface IExportStoreProps {
  exports: IExportRow[];
  totalCount: number;
  isErknm: boolean;
}

const ExportWithoutStore = ({ exports, isErknm, totalCount }: IExportStoreProps) => {
  const [tableColumns, setTableColumns] = useState<ITableColumn<IExportRow>[]>(initialExportTableColumns);
  const [itemsCheck, setItemsCheck] = useState<TableCheckItems>({});
  const [isDeleteButtonEnabled, setIsDeleteButtonEnabled] = useState(false);
  const [deletingTemplatesIds, setDeletingTemplatesIds] = useState<Array<number> | undefined>();

  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    dispatch(exportInitPage());
  }, [dispatch, isErknm]);

  const handleReloadClick = useCallback(() => dispatch(exportGetData()), [dispatch]);
  const deletingTemplatesNumber = deletingTemplatesIds?.length || 0;
  const deletingQuantityText = num2str(deletingTemplatesNumber, ['запись', 'записи', 'записей']);

  const handleDeleteClick = useCallback(() => {
    if (deletingTemplatesIds) {
      dispatch(exportRemove(deletingTemplatesIds));
    }
  }, [deletingTemplatesIds, dispatch]);

  const handleTableRowDelete = (id: number) => () => setDeletingTemplatesIds([id]);

  const handlePanelDelete = useCallback(() => {
    const deletingIds = Object.keys(itemsCheck)
      .filter(key => itemsCheck[key])
      .map(key => Number(key));

    setDeletingTemplatesIds(deletingIds);
  }, [itemsCheck]);

  const onLoadMore = useCallback(() => dispatch(exportGetDataNextPage()), [dispatch]);

  useEffect(() => {
    const keysArray = Object.keys(itemsCheck);
    const isAnyItemTrue = keysArray.some(key => itemsCheck[key]);

    setIsDeleteButtonEnabled(isAnyItemTrue);
  }, [itemsCheck]);

  useEffect(() => {
    const newItemsCheck: TableCheckItems = {};

    exports.forEach(item => {
      newItemsCheck[item.id] = false;
    });
    setItemsCheck(newItemsCheck);
  }, [exports]);

  useEffect(() => {
    const order = getSortOrder(initialExportTableColumns);

    dispatch(exportSetOrder(order));
  }, [dispatch]);

  const handleSortClick = useCallback(
    (id: keyof IExportRow) => {
      const newColumnsData = handleSortChange(tableColumns, id);

      setTableColumns(newColumnsData);
      const newOrder = getSortOrder(newColumnsData);

      dispatch(exportSetOrder(newOrder));
      dispatch(exportGetData());

      return newColumnsData;
    },
    [setTableColumns, dispatch, tableColumns],
  );

  const renderDropdownMenu = useCallback(({ id }: IExportRow) => {
    const dropdownItems: IDropdownItem[] = [
      {
        id: 'import-item-dropdown-delete-record',
        content: <DropdownItem onClick={handleTableRowDelete(id)}>Удалить запись</DropdownItem>,
      },
    ];

    return (
      <Dropdown items={dropdownItems}>
        <button className={styles.MenuButton} type="button">
          <IconMenu className={styles.MenuButtonIcon} />
        </button>
      </Dropdown>
    );
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setDeletingTemplatesIds(undefined);
    setItemsCheck(prev => {
      const newItemsCheck: TableCheckItems = {};

      Object.keys(prev).forEach(key => {
        newItemsCheck[key] = false;
      });

      return newItemsCheck;
    });
  }, []);

  return (
    <>
      <TablePage>
        <ExportPanel
          isDeleteButtonEnabled={isDeleteButtonEnabled}
          onReload={handleReloadClick}
          onDelete={handlePanelDelete}
        />
        <Table<IExportRow>
          columns={tableColumns}
          data={exports}
          itemsCheck={itemsCheck}
          handleLoadClick={onLoadMore}
          setItemsCheck={setItemsCheck}
          onSortChange={handleSortClick}
          totalCount={totalCount}
          columnStyles={columnStyles}
          renderDropdown={renderDropdownMenu}
          rowClassNames={rowAttributes}
        />
      </TablePage>
      <ConfirmModal
        visible={deletingTemplatesNumber > 0}
        title="Подтверждение"
        text={`Вы выбрали ${deletingTemplatesNumber} ${deletingQuantityText} для удаления. Удалить?`}
        applyButtonText="Удалить"
        cancelButtonText="Отменить"
        onApply={handleDeleteClick}
        onClose={handleDeleteModalClose}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState): IExportStoreProps => ({
  exports: getExportData(state),
  totalCount: getExportTotalCount(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
});

export const Export = connect(mapStateToProps)(ExportWithoutStore);
